import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, inject, OnChanges, SimpleChanges, DoCheck } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormGroup } from '@angular/forms';
import { MaterialModule } from '../../../shared/material.module';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { MatDialog } from '@angular/material/dialog';
import { LargeShareholder } from '../../../models/largeShareholder';
import { Country } from '../../../models/country';
import { LargeShareholderCategory } from '../../../models/largeShareholderCategory';
import { LutService } from '../../../services/lut.service';
import { LookupDataService } from '../../../services/lookupDataService.service';
import { Helpers } from '../../../utilities/helpers';
import { TextNormalizeDirective } from '../../../directives/text-normalize.directive';
import { LargeShareholderPyramidComponent } from '../large-shareholder-pyramid/large-shareholder-pyramid.component';
import { LargeShareholderConnectionsComponent } from '../large-shareholder-connections/large-shareholder-connections.component';
import { PyramidDto } from '../../../models/api/pyramidDto';
import { LargeShareholderConnectionDto } from '../../../models/api/largeShareholderConnectionDto';
import { PyramidEntitySummary } from '../../../models/pyramidEntitySummary';
import { PyramidEntriesByYearDto } from '../../../models/api/pyramidEntriesByYearDto';
import { DeleteEntityYearEvent } from '../pyramidal-item-card/pyramidal-item-card.component';
import { DeletePyramidYearEvent } from '../pyramids-year-card/pyramids-year-card.component';
import { LargeShareholderHoldingsResponseDto } from '../../../models/api/largeShareholderHoldingsDto';
import { LargeShareholderHoldingsComponent } from '../large-shareholder-holdings/large-shareholder-holdings.component';
import { LargeShareholderHoldingsPaginatedComponent } from '../large-shareholder-holdings-paginated/large-shareholder-holdings-paginated.component';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-large-shareholder-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    LargeShareholderPyramidComponent,
    LargeShareholderConnectionsComponent,
    LargeShareholderHoldingsComponent,
    LargeShareholderHoldingsPaginatedComponent,
    TextNormalizeDirective
  ],
  templateUrl: './large-shareholder-form.component.html',
  styleUrls: ['./large-shareholder-form.component.scss']
})
export class LargeShareholderFormComponent implements OnInit, OnDestroy, OnChanges, DoCheck {
  @Input() form!: FormGroup;
  @Input() submitButtonText = 'Submit';
  @Input() isEditMode = false;
  @Input() showYearWarning = false;
  @Input() pyramid: PyramidDto[] = [];
  @Input() pyramidEntitySummaries: PyramidEntitySummary[] = [];
  @Input() pyramidEntriesByYear?: PyramidEntriesByYearDto;
  @Input() connections: LargeShareholderConnectionDto[] = [];
  @Input() set holdings(value: LargeShareholderHoldingsResponseDto | null) {
    this._holdings = value;
  }
  @Input() initialTabIndex = 0;
  @Input() hideActions = false;
  @Output() submit = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();
  @Output() yearChange = new EventEmitter<string>();
  @Output() deleteConnection = new EventEmitter<string>();
  @Output() editPyramidItem = new EventEmitter<string>();
  @Output() deletePyramidItem = new EventEmitter<string>();
  @Output() deleteEntityYear = new EventEmitter<DeleteEntityYearEvent>();
  @Output() deletePyramidYear = new EventEmitter<DeletePyramidYearEvent>();
  @Output() tabChange = new EventEmitter<number>();

  selectedTabIndex = 0;
  _holdings: LargeShareholderHoldingsResponseDto | null = null;

  private lookupService = inject(LookupDataService);
  private lutService = inject(LutService);

  countries: Country[] = [];
  categories: LargeShareholderCategory[] = [];
  subcategories: LargeShareholderCategory[] = [];
  private allCategories: LargeShareholderCategory[] = []; // Cache all categories
  private categoriesLoaded = false; // Track if categories have been loaded
  private lastCategoryId: number | null = null; // Track last category ID to detect changes
  years: string[] = [];
  private subscriptions: Subscription[] = [];

  constructor(private confirmationDialogService: ConfirmationDialogService, private dialog: MatDialog) {
    // Load years from measurements helper
    this.years = Helpers.measurements();

    // Load countries from lookup service
    this.lookupService.getTableStream<Country>('countries').subscribe(data => {
      if (data) this.countries = data.sort((a, b) => a.name.localeCompare(b.name));
    });

    // Load large shareholder categories from LUT service and cache them
    const categoriesSub = this.lutService.getLargeShareholderCategories().subscribe(data => {
      if (data) {
        this.allCategories = data; // Cache all categories
        this.categories = data.filter(c => c.parentId === null).sort((a, b) => a.name.localeCompare(b.name));
        this.categoriesLoaded = true;

        // Initialize subcategories after categories are loaded
        this.initializeSubcategories();
      }
    });
    this.subscriptions.push(categoriesSub);
  }

  ngOnChanges(changes: SimpleChanges) {
    // Watch for form input changes - this handles when parent component patches the form
    if (changes['form'] && !changes['form'].firstChange) {
      // Form reference changed, re-initialize subcategories if categories are loaded
      if (this.categoriesLoaded) {
        this.initializeSubcategories();
      }
    }
  }

  ngDoCheck() {
    // Check on every change detection cycle if the category has changed
    // This catches changes even when patched with emitEvent: false
    if (this.categoriesLoaded && this.form) {
      const currentCategory = this.form.get('category')?.value;
      const currentCategoryId = currentCategory?.id ?? null;

      if (currentCategoryId !== this.lastCategoryId) {
        // Category has changed, update subcategories
        this.lastCategoryId = currentCategoryId;
        this.initializeSubcategories();
      }
    }
  }

  ngOnInit() {
    // Set initial tab index
    this.selectedTabIndex = this.initialTabIndex;

    // Watch year changes to reload data in edit mode
    if (this.isEditMode) {
      const yearSub = this.form.get('year')?.valueChanges.subscribe((year: string) => {
        if (year) {
          this.yearChange.emit(year);
        }
      });
      if (yearSub) this.subscriptions.push(yearSub);
    }

    // Watch category changes to update subcategories (when user selects from dropdown)
    const categorySub = this.form.get('category')?.valueChanges.subscribe((category: LargeShareholderCategory) => {
      if (category) {
        // Use cached categories instead of API call
        this.subcategories = this.allCategories
          .filter(c => c.parentId === category.id)
          .sort((a, b) => a.name.localeCompare(b.name));

        // Clear subcategory if the selected one is no longer valid
        const currentSubcategory = this.form.get('subcategory')?.value;
        if (currentSubcategory && currentSubcategory.parentId !== category.id) {
          this.form.patchValue({ subcategory: null });
        }
      } else {
        this.subcategories = [];
        this.form.patchValue({ subcategory: null });
      }
    });
    if (categorySub) this.subscriptions.push(categorySub);

    // Initial subcategory setup if categories are already loaded
    if (this.categoriesLoaded) {
      this.initializeSubcategories();
    }
  }

  /**
   * Initialize subcategories based on the current category value
   * This is called after categories are loaded from the API
   */
  private initializeSubcategories() {
    const currentCategory = this.form.get('category')?.value;
    if (currentCategory) {
      this.subcategories = this.allCategories
        .filter(c => c.parentId === currentCategory.id)
        .sort((a, b) => a.name.localeCompare(b.name));
    }
  }

  ngOnDestroy() {
    // Clean up all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  compareById(option1: any, option2: any): boolean {
    return option1 && option2 ? option1.id === option2.id : option1 === option2;
  }

  onSubmit() {
    if (this.form.valid) {
      const dialogObservable = this.isEditMode
        ? this.confirmationDialogService.openUpdateConfirmationDialog()
        : this.confirmationDialogService.openCreateConfirmationDialog();

      dialogObservable.subscribe((result: boolean) => {
        if (result) {
          this.submit.emit();
        }
      });
    }
  }

  onCancel() {
    this.cancel.emit();
  }

  isPyramidTabActive(): boolean {
    return this.isEditMode && this.selectedTabIndex === 3;
  }

  isConnectionTabActive(): boolean {
    return this.isEditMode && this.selectedTabIndex === 2;
  }

  onDeleteConnection(connectionId: string) {
    this.deleteConnection.emit(connectionId);
  }

  onEditPyramidItem(pyramidId: string) {
    this.editPyramidItem.emit(pyramidId);
  }

  onDeletePyramidItem(pyramidId: string) {
    this.deletePyramidItem.emit(pyramidId);
  }

  onDeleteEntityYear(event: DeleteEntityYearEvent) {
    this.deleteEntityYear.emit(event);
  }

  onDeletePyramidYear(event: DeletePyramidYearEvent) {
    this.deletePyramidYear.emit(event);
  }

  parseInt(value: string): number | undefined {
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? undefined : parsed;
  }

  onTabChange(index: number) {
    this.selectedTabIndex = index;
    this.tabChange.emit(index);
  }

  get isLargeDataset(): boolean {
    return (this._holdings?.holdings?.length ?? 0) > 500;
  }
}
